'use client'

/**
 * StockDailyChart — the "bigger picture" daily candlestick chart on /stocks/[symbol]. Same visual
 * language as the intraday chart (candles + 9/50/200 EMA in the shared blue/yellow/green palette +
 * volume pane), just at the daily scale, with a range toggle (3M/6M/1Y) and a hover crosshair showing
 * that day's O/H/L/C. Pure canvas. Data: /api/stocks/daily-chart (fetches extra history so the 200-EMA
 * is valid across the whole visible range).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'next-themes'
import ChartStatsReadout from '@/components/ChartStatsReadout'
import { whenVisible } from '@/lib/whenVisible'

type Bar = { t: number; o: number; h: number; l: number; c: number; v: number }
const RANGES = [{ label: '3M', days: 63 }, { label: '6M', days: 126 }, { label: '1Y', days: 252 }] as const
// Matches StockLivePulse's intraday EMAs so the two charts read the same.
const EMA_DEFS = [{ period: 9, color: '#3b82f6' }, { period: 50, color: '#eab308' }, { period: 200, color: '#22c55e' }] as const
const fmtPx = (n: number) => (n < 1 ? `$${n.toFixed(3)}` : `$${n.toFixed(2)}`)
const fmtVol = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : `${n}`)
const fmtDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: '2-digit' })
function ema(v: number[], p: number): (number | null)[] {
  const k = 2 / (p + 1); const out: (number | null)[] = []; let prev: number | null = null
  for (let i = 0; i < v.length; i++) {
    if (prev === null) { if (i >= p - 1) { let s = 0; for (let j = i - p + 1; j <= i; j++) s += v[j]; prev = s / p; out.push(prev) } else out.push(null) }
    else { prev = v[i] * k + prev * (1 - k); out.push(prev) }
  }
  return out
}

export default function StockDailyChart({ symbol }: { symbol: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [bars, setBars] = useState<Bar[]>([])
  const [status, setStatus] = useState<'loading' | 'ok' | 'empty'>('loading')
  const [rangeIdx, setRangeIdx] = useState(1) // 6M default
  // ── FREE ZOOM / PAN (owner 2026-07-28: "like zoom in and zoom out ... more functional like the
  // chart page"). The 3M/6M/1Y buttons stay as quick presets; `zoom` scales the preset's bar count and
  // `panEnd` moves the right edge back through history, so wheel + drag behave like the main chart.
  // Kept as bar COUNTS rather than pixel offsets so a resize cannot change what you are looking at.
  const [zoom, setZoom] = useState(1)            // >1 = fewer bars on screen = zoomed in
  const [panEnd, setPanEnd] = useState(0)        // bars trimmed from the RIGHT edge (0 = latest)
  const dragRef = useRef<{ x: number; panEnd: number } | null>(null)
  // Daily RVOL denominator: one median session volume for the symbol (see /api/stocks/daily-chart).
  const [dailyBase, setDailyBase] = useState<{ v: number; n: number } | null>(null)
  const hoverRef = useRef<number | null>(null)
  // The bar the O/H/L/C legend shows. Kept in state (not just the draw ref) because the legend is a fixed
  // HTML element top-left, so it never scrolls off-screen the way a cursor-following tooltip did.
  const [legendBar, setLegendBar] = useState<{ b: Bar; prev: number | null } | null>(null)
  const lastHoverIdx = useRef(-1)
  // Canvas paints its own gridlines/crosshair, so it reads the resolved theme and branches; the DARK
  // value is kept verbatim so dark stays byte-identical. Candles/EMAs/volume read on either ground.
  const { resolvedTheme } = useTheme()
  const dark = resolvedTheme !== 'light'

  useEffect(() => {
    let alive = true
    setStatus('loading')
    // Fetch extra history (460d) so the 200-day EMA is populated across even the 1Y view. Then POLL so
    // today's forming candle (and the price) keep updating while the user stays on the page (owner 2026-07-15).
    const pull = () =>
      fetch(`/api/stocks/daily-chart?symbol=${encodeURIComponent(symbol)}&days=460`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { bars: Bar[] } | null) => {
          if (!alive) return
          // Drop bars with a null/non-finite O/H/L/C (thin microcaps like $SOBR can have no-trade/bad days).
          // A single null bar would crash the O/H/L/C legend (f2(null).toFixed) and poison the EMA math.
          const clean = (j && Array.isArray(j.bars) ? j.bars : [])
            .filter((b) => b && Number.isFinite(b.o) && Number.isFinite(b.h) && Number.isFinite(b.l) && Number.isFinite(b.c))
            .map((b) => ({ ...b, v: Number.isFinite(b.v) ? b.v : 0 }))
          const jj = j as unknown as { baseVol?: number | null; baseN?: number | null } | null
          setDailyBase(jj?.baseVol && jj.baseVol > 0 ? { v: jj.baseVol, n: jj.baseN ?? 0 } : null)
          if (clean.length >= 5) { setBars(clean); setStatus('ok') } else setStatus((s) => (s === 'ok' ? 'ok' : 'empty'))
        })
        .catch(() => { if (alive) setStatus((s) => (s === 'ok' ? 'ok' : 'empty')) })
    pull()
    const id = setInterval(whenVisible(pull), 45_000)
    return () => { alive = false; clearInterval(id) }
  }, [symbol])

  // EMAs computed over the FULL series; the view is the last N bars, and we slice EMAs to match.
  const { view, emas } = useMemo(() => {
    const closes = bars.map((b) => b.c)
    const full = EMA_DEFS.map((d) => ema(closes, d.period))
    // Preset gives the base window; zoom scales it; panEnd slides that window back through history.
    // Floor of 20 bars so zooming in cannot collapse the chart to a single candle.
    const want = Math.max(20, Math.round(RANGES[rangeIdx].days / zoom))
    const n = Math.min(bars.length, want)
    const end = Math.max(n, bars.length - panEnd)      // right edge, clamped so a full window always fits
    const start = Math.max(0, end - n)
    return { view: bars.slice(start, end), emas: full.map((e) => e.slice(start, end)) }
  }, [bars, rangeIdx, zoom, panEnd])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || view.length < 2) return
    const ctx = canvas.getContext('2d'); if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const W = rect.width, H = rect.height
    canvas.width = Math.floor(W * dpr); canvas.height = Math.floor(H * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const PADL = 6, PADR = 52, PADT = 8, PADB = 18, VOLH = 40
    const n = view.length
    // Scale to the PRICE action so candles fill the height (owner 2026-07-15: "zoom in"). EMAs may pull
    // the range out only up to half the price range on each side, so a far-away 200-EMA never squishes
    // the candles; beyond that the EMA line rides clamped to the chart edge when drawn.
    let pLo = Infinity, pHi = -Infinity
    for (const b of view) { if (b.l < pLo) pLo = b.l; if (b.h > pHi) pHi = b.h }
    const pRange = (pHi - pLo) || 1
    let lo = pLo, hi = pHi
    for (const e of emas) for (const val of e) if (val != null) { if (val < lo) lo = val; if (val > hi) hi = val }
    lo = Math.max(lo, pLo - pRange * 0.5)
    hi = Math.min(hi, pHi + pRange * 0.5)
    const pad = (hi - lo) * 0.06 || 1; lo -= pad; hi += pad
    const priceBottom = H - PADB - VOLH - 6
    const chartH = priceBottom - PADT, chartW = W - PADL - PADR
    const maxVol = Math.max(1, ...view.map((b) => b.v))
    const xc = (i: number) => PADL + (i + 0.5) * (chartW / n)
    const yFor = (p: number) => PADT + (1 - (p - lo) / (hi - lo)) * chartH
    const yClamp = (p: number) => Math.max(PADT, Math.min(priceBottom, yFor(p)))
    const cw = Math.max(1, (chartW / n) * 0.66)

    ctx.clearRect(0, 0, W, H)
    ctx.font = '10px -apple-system, system-ui, sans-serif'; ctx.textBaseline = 'middle'
    for (let g = 0; g <= 4; g++) {
      const p = lo + ((hi - lo) * g) / 4, y = yFor(p)
      ctx.strokeStyle = dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(PADL, y); ctx.lineTo(W - PADR, y); ctx.stroke()
      ctx.fillStyle = '#71717a'; ctx.textAlign = 'left'; ctx.fillText(fmtPx(p), W - PADR + 4, y)
    }
    ctx.textBaseline = 'alphabetic'
    for (let i = 0; i < n; i++) { const b = view[i]; const bh = (b.v / maxVol) * VOLH; ctx.fillStyle = b.c >= b.o ? 'rgba(52,211,153,0.4)' : 'rgba(251,113,133,0.4)'; ctx.fillRect(xc(i) - cw / 2, H - PADB - bh, cw, bh) }
    for (let i = 0; i < n; i++) {
      const b = view[i]; const green = b.c >= b.o; const x = xc(i)
      ctx.strokeStyle = green ? '#34d399' : '#fb7185'; ctx.fillStyle = green ? '#34d399' : '#fb7185'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x, yFor(b.h)); ctx.lineTo(x, yFor(b.l)); ctx.stroke()
      const yo = yFor(b.o), ycl = yFor(b.c); ctx.fillRect(x - cw / 2, Math.min(yo, ycl), cw, Math.max(1, Math.abs(ycl - yo)))
    }
    // EMA overlays (9/50/200) — clamped to the chart area so a far 200-EMA hugs the edge, not the scale.
    emas.forEach((arr, di) => {
      ctx.beginPath(); let started = false
      for (let i = 0; i < n; i++) { const val = arr[i]; if (val == null) continue; const x = xc(i), y = yClamp(val); started ? ctx.lineTo(x, y) : (ctx.moveTo(x, y), started = true) }
      ctx.strokeStyle = EMA_DEFS[di].color; ctx.lineWidth = 1.5; ctx.stroke()
    })
    ctx.font = '10px -apple-system, system-ui, sans-serif'; ctx.fillStyle = '#52525b'
    ctx.textAlign = 'left'; ctx.fillText(fmtDate(view[0].t), PADL, H - 5)
    ctx.textAlign = 'right'; ctx.fillText(fmtDate(view[n - 1].t), W - PADR, H - 5)

    // Crosshair + dot only (the O/H/L/C readout is a fixed HTML legend top-left, so it never clips).
    const hi2 = hoverRef.current
    if (hi2 != null && hi2 >= 0 && hi2 < n) {
      const b = view[hi2]; const x = xc(hi2)
      ctx.strokeStyle = dark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.22)'; ctx.setLineDash([3, 3]); ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(x, PADT); ctx.lineTo(x, priceBottom); ctx.stroke(); ctx.setLineDash([])
      ctx.beginPath(); ctx.arc(x, yFor(b.c), 3, 0, Math.PI * 2); ctx.fillStyle = b.c >= b.o ? '#34d399' : '#fb7185'; ctx.fill()
    }
  }, [view, emas, dark])

  useEffect(() => { if (status === 'ok') draw() }, [status, draw, rangeIdx])
  useEffect(() => { const on = () => draw(); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on) }, [draw])

  // ── WHEEL ZOOM MUST BE A NATIVE, NON-PASSIVE LISTENER (owner 2026-07-28: "zoom does not work, it
  // scrolls the page") ──────────────────────────────────────────────────────────────────────────────
  // React attaches onWheel passively, and preventDefault() inside a passive listener is IGNORED — the
  // browser logs a warning at most, so the handler appeared to run while the page scrolled anyway.
  // addEventListener with { passive: false } is the only way to claim the gesture.
  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault()                       // now actually honoured — the page stops scrolling
      setZoom((z) => Math.min(8, Math.max(1, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))))
    }
    el.addEventListener('wheel', onWheelNative, { passive: false })
    return () => el.removeEventListener('wheel', onWheelNative)
  }, [status])   // re-bind when the canvas mounts (it only renders once status === 'ok')

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current; if (!canvas || view.length < 2) return
    const rect = canvas.getBoundingClientRect()
    const PADL = 6, PADR = 52, chartW = rect.width - PADL - PADR
    const raw = Math.round(((e.clientX - rect.left - PADL) / chartW) * view.length - 0.5)
    const idx = Math.max(0, Math.min(view.length - 1, raw))
    hoverRef.current = idx; draw()
    if (idx !== lastHoverIdx.current) { lastHoverIdx.current = idx; setLegendBar({ b: view[idx], prev: idx > 0 ? view[idx - 1].c : null }) }
  }
  const onLeave = () => { hoverRef.current = null; lastHoverIdx.current = -1; setLegendBar(null); draw(); dragRef.current = null }

  // Drag = pan. Converted from pixels to BARS using the current window, so one bar of cursor travel
  // moves exactly one bar regardless of zoom level or canvas width.
  const onDown = (e: React.MouseEvent<HTMLCanvasElement>) => { dragRef.current = { x: e.clientX, panEnd } }
  const onDrag = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const d = dragRef.current
    if (!d || !canvasRef.current || !view.length) return
    const w = canvasRef.current.getBoundingClientRect().width - 58
    const barsPerPx = view.length / Math.max(1, w)
    const moved = Math.round((e.clientX - d.x) * barsPerPx)
    setPanEnd(Math.max(0, Math.min(bars.length - 20, d.panEnd + moved)))
  }
  const endDrag = () => { dragRef.current = null }
  const resetView = () => { setZoom(1); setPanEnd(0) }

  const pct = view.length >= 2 && view[0].c > 0 ? ((view[view.length - 1].c - view[0].c) / view[0].c) * 100 : null
  // Legend shows the hovered bar, or the latest bar when not hovering (TradingView-style, always visible).
  const f2 = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? '—' : n < 1 ? n.toFixed(3) : n.toFixed(2))
  const legend = legendBar ?? (view.length ? { b: view[view.length - 1], prev: view.length > 1 ? view[view.length - 2].c : null } : null)
  const legChg = legend && legend.prev && legend.prev > 0 ? ((legend.b.c - legend.prev) / legend.prev) * 100 : null

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-baseline gap-2">
          <span className="text-[12px] font-bold uppercase tracking-wider text-muted-foreground">Daily</span>
          <span className="text-[9px] text-muted-foreground/70">scroll to zoom · drag to pan · double-click to reset</span>
          {pct != null && <span className={`text-[11px] font-semibold tabular-nums ${pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}% / {RANGES[rangeIdx].label}</span>}
        </div>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {RANGES.map((r, i) => (
            <button key={r.label} onClick={() => setRangeIdx(i)} className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${rangeIdx === i ? 'bg-amber-500 text-black' : 'text-muted-foreground hover:text-foreground'}`}>{r.label}</button>
          ))}
        </div>
      </div>
      <div className="relative" style={{ height: 300 }}>
        {status === 'ok' && (
          <canvas
            ref={canvasRef}
            className={`h-full w-full ${dragRef.current ? 'cursor-grabbing' : 'cursor-crosshair'}`}
            onMouseMove={(e) => { onMove(e); onDrag(e) }}
            onMouseLeave={onLeave}
            onMouseDown={onDown}
            onMouseUp={endDrag}
            onDoubleClick={resetView}
          />
        )}
        {/* Same readout component as the chart page and the intraday tab, so all three agree. */}
        {status === 'ok' && legend && (
          <ChartStatsReadout
            variant="overlay"
            hovering={legendBar != null}
            bar={{ t: legend.b.t, open: legend.b.o, high: legend.b.h, low: legend.b.l, close: legend.b.c, volume: legend.b.v }}
            rvol={dailyBase ? { v: legend.b.v / dailyBase.v, thin: dailyBase.n < 10 } : null}
          />
        )}
        {/* Zoom affordance — only once the view has been moved, so it never nags on first load. */}
        {status === 'ok' && (zoom !== 1 || panEnd !== 0) && (
          <button
            onClick={resetView}
            className="absolute bottom-2 right-2 z-10 rounded-md border border-border bg-card/80 px-2 py-1 text-[10px] font-semibold text-foreground/80 backdrop-blur-sm hover:border-amber-500/50 hover:text-amber-700 dark:hover:text-amber-300"
          >
            Reset view
          </button>
        )}
        {status === 'loading' && <div className="flex h-full items-center justify-center text-xs text-muted-foreground"><span className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-400 border-r-transparent" /> Loading daily chart…</div>}
        {status === 'empty' && <div className="flex h-full items-center justify-center text-xs text-muted-foreground/70">No daily history for ${symbol} yet.</div>}
      </div>
      {status === 'ok' && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
          {EMA_DEFS.map((d) => (
            <span key={d.period} className="inline-flex items-center gap-1"><span className="h-0.5 w-3 rounded" style={{ background: d.color }} /> {d.period} EMA</span>
          ))}
          <span className="ml-auto">hover to scan</span>
        </div>
      )}
    </div>
  )
}
